import { observer } from 'mobx-react'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import rootStore from '@/app/store'
import Dialog from '@/ui/dialogs/Dialog'
import CTA from '@/ui/CTA'
import Form from '@/ui/form/Form'
import Input from '@/ui/form/Input'
import Textarea from '@/ui/form/Textarea'
import FileUploader from '@/components/FileUploader'
import { Icons } from '@/ui/icon'
import { EscalationFormData } from '@/features/support/types'
import { useDialogRef } from '@/utils/dialog'
import { sendSupportEmail } from '@/features/support/api'

interface ContactFormProps {
  onClose: () => void
  defaultEmail?: string
  transcript?: string
}

const ContactForm = ({
  onClose,
  defaultEmail,
  transcript,
}: ContactFormProps) => {
  const { auth, support } = rootStore
  const { dialogRef, handleClose } = useDialogRef()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<EscalationFormData>({
    defaultValues: {
      email: defaultEmail || auth.user?.email || '',
      dateOfIncident: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm format
      files: [],
      description: '',
    },
    mode: 'onChange',
  })

  const onSubmit = async (data: EscalationFormData) => {
    try {
      await sendSupportEmail({
        email: data.email,
        description: data.description,
        dateOfIncident: data.dateOfIncident,
        files: data.files || [],
        transcript:
          transcript ?? support.contactFormStore.transcript ?? undefined,
      })
      toast.success(
        "Your escalation request has been submitted. We'll follow up via email.",
      )
      handleDialogClose()
    } catch (error) {
      toast.error('Failed to submit escalation request. Please try again.')
      console.error('Error submitting escalation:', error)
    }
  }

  const handleDialogClose = () => {
    handleClose()
    onClose()
  }

  const handleFileUpload = (files: File[] | null) => {
    setValue('files', files || [])
  }

  return (
    <Dialog
      ref={dialogRef}
      title="Escalar a soporte humano"
      text="Si tienes un problema con el software, por favor, escribe un mensaje detallado y adjunta los archivos necesarios."
      icon={Icons.Help}
      onClose={handleDialogClose}
      actions={
        <>
          <CTA
            variant="default"
            size="medium"
            onClick={handleDialogClose}
            isDisabled={isSubmitting}
          >
            Cancelar
          </CTA>
          <CTA
            variant="system"
            size="medium"
            onClick={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar'}
          </CTA>
        </>
      }
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="Correo de contacto*"
          type="email"
          {...register('email', {
            required: 'El correo de contacto es requerido',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'El correo de contacto debe ser válido',
            },
          })}
          error={errors.email?.message}
          autoFocus
        />

        <Input
          label="Fecha y hora"
          type="datetime-local"
          {...register('dateOfIncident')}
          error={errors.dateOfIncident?.message}
        />
        <div className="formControl">
          <label className="formControl-label" htmlFor="files">
            Adjuntar archivos (opcional)
          </label>

          <Controller
            name="files"
            control={control}
            render={({ field }) => (
              <FileUploader
                mode="input"
                value={field.value ?? []}
                onFilesUpdate={handleFileUpload}
                labelIdle="Attach files (optional - max 5 files)"
                acceptedFileTypes={['image/*', 'application/pdf']}
                showFilesList={true}
              />
            )}
          />
          {errors.files && (
            <div className="form-error">{errors.files.message}</div>
          )}
        </div>

        <Textarea
          label="Descripción*"
          {...register('description', {
            required: 'La descripción es requerida',
            minLength: {
              value: 20,
              message: 'La descripción debe tener al menos 20 caracteres',
            },
            maxLength: {
              value: 2000,
              message: 'La descripción no debe exceder 2000 caracteres',
            },
          })}
          error={errors.description?.message}
          rows={4}
          placeholder="Describe el problema en detalle (mínimo 20 caracteres)..."
        />

        <div className="alert-info">
          <span className="alert-content">
            &nbsp;Todo el hilo de la conversación será incluido automáticamente
            en tu escalamiento.
          </span>
        </div>
      </Form>
    </Dialog>
  )
}

ContactForm.displayName = 'ContactForm'

export default observer(ContactForm)
